import { eq, and, desc, gte } from 'drizzle-orm';
import { BaseRepository } from '../../../classes/BaseRepository';
import { healthRecordUploads } from '../models/healthRecordUploads';
import type { HealthRecordUpload } from '../types/HealthRecordUpload';
import type { UploadStatus } from '../types/UploadStatus';
import { UploadStatusEnum } from '../enums/UploadStatusEnum';
import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { convertToAppError, type AsyncResult, type AppError, ok, err } from '@shared/utils';
import { DATA_API_MESSAGES, HTTP_STATUS_NAMES } from '@shared/constants';

/**
 * @group EHR
 * @summary Health Record Upload repository
 * 
 * <blockquote>
 * Provides data access methods for health record upload management, including CRUD operations and upload tracking.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const uploadRepository = new HealthRecordUploadRepository();
 * const uploads = await uploadRepository.findByPatient(patientId, 10);
 * uploads.match({
 *   ok: (results) => console.log('Upload history:', results),
 *   err: (error) => console.error('Error:', error.errorMessage)
 * });
 * ```
 */
export class HealthRecordUploadRepository extends BaseRepository<HealthRecordUpload> {
  protected table = healthRecordUploads;

  /**
   * Get upload history for a patient, ordered by most recent first
   * 
   * @param patientId - The patient ID to search for
   * @param limit - Maximum number of results to return (default: 50)
   * @returns AsyncResult containing array of upload records
   */
  async findByPatient(
    patientId: string,
    limit: number = 50
  ): AsyncResult<HealthRecordUpload[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<HealthRecordUpload[]>(async () =>
          (await database
            .select()
            .from(healthRecordUploads)
            .where(eq(healthRecordUploads.patientId, patientId))
            .orderBy(desc(healthRecordUploads.createdAt))
            .limit(limit)) as HealthRecordUpload[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from findByPatient: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Find uploads by EHR integration ID
   * 
   * @param ehrIntegrationId - The EHR integration ID to search for
   * @param limit - Maximum number of results to return (default: 50)
   * @returns AsyncResult containing array of upload records
   */
  async findByIntegration(
    ehrIntegrationId: string,
    limit: number = 50
  ): AsyncResult<HealthRecordUpload[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<HealthRecordUpload[]>(async () =>
          (await database
            .select()
            .from(healthRecordUploads)
            .where(eq(healthRecordUploads.ehrIntegrationId, ehrIntegrationId))
            .orderBy(desc(healthRecordUploads.createdAt))
            .limit(limit)) as HealthRecordUpload[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from findByIntegration: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Find uploads by status
   * 
   * @param uploadStatus - The upload status to search for
   * @param limit - Maximum number of results to return (default: 100)
   * @returns AsyncResult containing array of upload records
   */
  async findByStatus(
    uploadStatus: UploadStatus,
    limit: number = 100
  ): AsyncResult<HealthRecordUpload[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<HealthRecordUpload[]>(async () =>
          (await database
            .select()
            .from(healthRecordUploads)
            .where(eq(healthRecordUploads.uploadStatus, uploadStatus))
            .orderBy(desc(healthRecordUploads.createdAt))
            .limit(limit)) as HealthRecordUpload[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from findByStatus: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Find upload by external ID
   * 
   * @param externalId - The external ID to search for
   * @returns AsyncResult containing the upload record or null if not found
   */
  async findByExternalId(externalId: string): AsyncResult<HealthRecordUpload | null, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<HealthRecordUpload[]>(async () =>
          (await database
            .select()
            .from(healthRecordUploads)
            .where(eq(healthRecordUploads.externalId, externalId))
            .limit(1)) as HealthRecordUpload[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from findByExternalId: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows[0] || null);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Update upload status with optional error message and EHR document ID
   * 
   * @param id - The upload record ID to update
   * @param status - The new upload status
   * @param ehrDocumentId - Optional EHR document ID
   * @param errorMessage - Optional error message
   * @returns AsyncResult containing the updated upload record
   */
  async updateStatus(
    id: string,
    status: UploadStatus,
    ehrDocumentId?: string,
    errorMessage?: string
  ): AsyncResult<HealthRecordUpload, AppError> {
    return db.match({
      ok: async (database) => {
        const updateData: Partial<HealthRecordUpload> = {
          uploadStatus: status,
          updatedAt: new Date()
        };

        if (ehrDocumentId !== undefined) {
          updateData.ehrDocumentId = ehrDocumentId;
        }

        if (errorMessage !== undefined) {
          updateData.errorMessage = errorMessage;
        }

        const queryResult = await retryableQuery<HealthRecordUpload[]>(async () =>
          (await database
            .update(healthRecordUploads)
            .set(updateData)
            .where(eq(healthRecordUploads.id, id))
            .returning()) as HealthRecordUpload[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from updateStatus: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            if (rows.length === 0) {
              return err(convertToAppError(
                `No upload record found with id: ${id}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }

            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get recent failed uploads for a patient within a specified time window
   * 
   * @param patientId - The patient ID to search for
   * @param hours - Number of hours to look back (default: 24)
   * @returns AsyncResult containing array of failed upload records
   */
  async getRecentFailures(
    patientId: string,
    hours: number = 24
  ): AsyncResult<HealthRecordUpload[], AppError> {
    return db.match({
      ok: async (database) => {
        const cutoffTime = new Date();
        cutoffTime.setHours(cutoffTime.getHours() - hours);

        const queryResult = await retryableQuery<HealthRecordUpload[]>(async () =>
          (await database
            .select()
            .from(healthRecordUploads)
            .where(
              and(
                eq(healthRecordUploads.patientId, patientId),
                eq(healthRecordUploads.uploadStatus, UploadStatusEnum.FAILED),
                gte(healthRecordUploads.createdAt, cutoffTime)
              )
            )
            .orderBy(desc(healthRecordUploads.createdAt))) as HealthRecordUpload[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from getRecentFailures: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }
}
